<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <div
    class="max-w-xl w-full rounded-lg sm:shadow-lg px-8 py-8 sm:my-2
                                                                    flex flex-col items-center justify-center"
  >
    <IconArrowLeft
      class="self-start"
      @click="switchToLoginPanel"
    />

    <Logo class="place-self-end self-center -mt-6 mb-2" />

    <h1
      id="registration-title"
      class="text-2xl mb-8"
      style="color: #000000;"
    >
      {{ signUpMessages.title }}
    </h1>

    <form
      v-if="!signUpSucced"
      class="flex-col justify-center w-80 space-y-4"
      aria-labelledby="registration-title"
      @submit.prevent="federatedSignIn ? onFederatedSignUpPressWithValidation() : onSignUpPressWithValidation()"
    >
      <div class="space-y-1">
        <label
          for="reg-email"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.email_address') }}
        </label>
        <Input
          id="reg-email"
          ref="emailInput"
          v-model="user.email"
          type="email"
          name="email"
          autocomplete="email"
          autofocus
          :placeholder="$t('message.email_placeholder')"
          :error="emailFieldError"
          :error-message="emailErrorMessage"
          :valid="emailValid"
          :disabled="federatedSignIn && user.email"
          :aria-label="$t('message.email_address')"
          aria-required="true"
        />
      </div>

      <div class="space-y-1">
        <label
          for="reg-name"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.name') }}
        </label>
        <Input
          id="reg-name"
          ref="nameInput"
          v-model="user.name"
          type="text"
          name="fname"
          autocomplete="given-name"
          :placeholder="$t('message.name')"
          :error="nameFieldError"
          :error-message="nameErrorMessage"
          :valid="nameValid"
          :aria-label="$t('message.name')"
          aria-required="true"
        />
      </div>

      <div class="space-y-1">
        <label
          for="reg-surname"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.surname') }}
        </label>
        <Input
          id="reg-surname"
          ref="surnameInput"
          v-model="user.surname"
          type="text"
          name="lname"
          autocomplete="family-name"
          :placeholder="$t('message.surname')"
          :error="surnameFieldError"
          :error-message="surnameErrorMessage"
          :valid="surnameValid"
          :aria-label="$t('message.surname')"
          aria-required="true"
        />
      </div>

      <div
        v-if="!federatedSignIn"
        class="space-y-1"
      >
        <label
          for="reg-password"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.password') }}
        </label>
        <PasswordInput
          id="reg-password"
          ref="passwordInput"
          v-model="user.password"
          autocomplete="new-password"
          validate-password
          :placeholder="$t('message.your_password')"
          :is-valid="isValidPassword"
          :error="passwordFieldError"
          :error-message="passwordErrorMessage"
          :aria-label="$t('message.password')"
          aria-required="true"
          @update:isValid="updatePasswordStatus"
          @update:requirements="updatePasswordRequirements"
        />
      </div>

      <div
        v-if="!federatedSignIn"
        class="space-y-1"
      >
        <label
          for="reg-confirm-password"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.repeat_password') }}
        </label>
        <PasswordInput
          id="reg-confirm-password"
          ref="confirmPasswordInput"
          v-model="confirmPassword"
          autocomplete="new-password"
          :placeholder="$t('message.repeat_password_placeholder')"
          :error="confirmPasswordError"
          :error-message="confirmPasswordErrorMessage"
          :valid="passwordsMatch && !!confirmPassword"
          :aria-label="$t('message.repeat_password')"
          aria-required="true"
        />
      </div>

      <div class="space-y-1">
        <label
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          * {{ $t('message.birthday_with_format') }}
        </label>

        <div class="flex flex-row items-center justify-between">
          <Select
            id="birth-day"
            v-model="dayBirthdate"
            class="h-10"
            :default-selector-text="dayBirthdate"
            :options="dateCollector.days"
            :error="birthdateFieldError"
            aria-describedby="birthdate-error"
            :aria-label="$t('message.birth_day')"
            aria-required="true"
          />
          <Select
            id="birth-month"
            v-model="monthBirthdate"
            class="h-10"
            :default-selector-text="monthBirthdate"
            :options="dateCollector.months"
            :error="birthdateFieldError"
            aria-describedby="birthdate-error"
            :aria-label="$t('message.birth_month')"
            aria-required="true"
          />
          <Select
            id="birth-year"
            v-model="yearBirthdate"
            class="h-10"
            :options="dateCollector.years"
            :default-selector-text="defaultYearBirthdateText"
            :default-selector-value="defaultYearBirthdateValue"
            :error="birthdateFieldError"
            aria-describedby="birthdate-error"
            :aria-label="$t('message.birth_year')"
            aria-required="true"
          />
        </div>
        <p
          v-if="birthdateFieldError"
          id="birthdate-error"
          class="input-error-message"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {{ birthdateErrorMessage }}
        </p>
      </div>

      <div class="space-y-1">
        <label
          for="tel"
          class="block text-sm flex justify-start"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          {{ registrationRequestPanelUtils.phoneNumberRequired ? `* ${$t('message.phoneNumber')}` : $t('message.phoneNumber') }}
        </label>

        <PhoneNumber
          id="tel"
          type="tel"
          name="tel"
          autocomplete="tel"
          :error="phoneNumberError"
          :error-message="phoneNumberErrorMessage"
          :aria-label="$t('message.phoneNumber')"
          :aria-required="registrationRequestPanelUtils.phoneNumberRequired ? 'true' : undefined"
          @get-phone-number="($event) => phoneNumber = $event"
        />
      </div>

      <div class="flex flex-col justify-start items-start">
        <label
          class="mb-0 -mt-2 pt-5 pb-2 pl-1 leading-tighter font-bold
                                                              cursor-text align-middle text-xs italic"
          :style="{ color: global.primaryColor }"
        >{{
          $t('message.mandatory_field') }}</label>
      </div>

      <div
        class="text-left"
        :style="{ color: global.boxColor || '#696E79' }"
      >
        <span
          class="text-sm"
          v-html="$t('message.sign_up_general_conditions', {
            color: linkColor, generalConditionsUrl: global.generalConditionsUrl
          })"
        />
        <span
          class="text-sm"
          v-html="$t('message.sign_up_vexatious_clauses')"
        />
        <br>
        <span
          class="text-sm"
          v-html="$t('message.sign_up_privacy_policy', {
            color: linkColor, privacyUrl: global.privacyUrl
          })"
        />
      </div>

      <div class="border border-gray-200 rounded-full" />

      <!-- Marketing permission -->
      <div class="flex flex-row items-center justify-between">
        <label
          class="font-bold text-justify"
          style="color: #000000;"
          v-html="signUpMessages.marketingTitle"
        />
        <Switch v-model:checked="acceptMarketingPermission" />
      </div>
      <div>
        <p
          class="text-justify text-sm"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          <span v-if="!marketingTextIsExpanded">
            {{ marketingConsentDescriptionNotExpanded }}
            <span
              class="pl-2 font-bold underline cursor-pointer"
              :style="{ color: global.primaryColor }"
              @click="marketingTextIsExpanded = !marketingTextIsExpanded"
            >
              <br>
              <div class="flex justify-end">{{ $t('message.read_more') }}</div>
            </span>
          </span>
          <span v-else>
            {{ marketingConsentDescriptionExpanded }}
            <br>
            <span
              class="font-bold underline cursor-pointer flex justify-end"
              :style="{ color: global.primaryColor }"
              @click="marketingTextIsExpanded = !marketingTextIsExpanded"
            >
              {{ $t('message.hide') }}
            </span>
          </span>
        </p>
      </div>

      <!-- <div class="border border-gray-200 rounded-full" /> -->
      <br>

      <!-- Profiling permission -->
      <div class="flex flex-row items-center justify-between">
        <label
          class="font-bold text-justify"
          style="color: #000000;"
          v-html="signUpMessages.profilingTitle"
        />
        <Switch v-model:checked="acceptProfilingPermission" />
      </div>

      <div>
        <p
          class="text-justify text-sm"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          <span v-if="!profilingTextIsExpanded">
            {{ profilingConsentDescriptionNotExpanded }}
            <span
              class="pl-2 font-bold underline cursor-pointer"
              :style="{ color: global.primaryColor }"
              @click="profilingTextIsExpanded = !profilingTextIsExpanded"
            >
              <br>
              <div class="flex justify-end">{{ $t('message.read_more') }}</div>
            </span>
          </span>
          <span v-else>
            {{ profilingConsentDescriptionExpanded }}
            <br>
            <span
              class="font-bold underline cursor-pointer flex justify-end"
              :style="{ color: global.primaryColor }"
              @click="profilingTextIsExpanded = !profilingTextIsExpanded"
            >
              {{ $t('message.hide') }}
            </span>
          </span>
        </p>
      </div>

      <!-- Third permission -->
      <div
        v-if="!registrationRequestPanelUtils.hideThirdConsent"
        class="flex flex-row items-center justify-between"
      >
        <label
          class="font-bold text-justify"
          style="color: #000000;"
          v-html="signUpMessages.thirdTitle"
        />
        <Switch v-model:checked="acceptThirdPermission" />
      </div>
      <div v-if="!registrationRequestPanelUtils.hideThirdConsent">
        <p
          class="text-justify text-sm"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          <span v-if="!thirdTextIsExpanded">
            {{ thirdConsentDescriptionNotExpanded }}
            <span
              class="pl-2 font-bold underline cursor-pointer"
              :style="{ color: global.primaryColor }"
              @click="thirdTextIsExpanded = !thirdTextIsExpanded"
            >
              <br>
              <div class="flex justify-end">{{ $t('message.read_more') }}</div>
            </span>
          </span>
          <span v-else>
            {{ thirdConsentDescriptionExpanded }}
            <br>
            <span
              class="font-bold underline cursor-pointer flex justify-end"
              :style="{ color: global.primaryColor }"
              @click="thirdTextIsExpanded = !thirdTextIsExpanded"
            >
              {{ $t('message.hide') }}
            </span>
          </span>
        </p>
      </div>

      <Button
        v-if="!federatedSignIn"
        class="w-80"
        aria-describedby="validation-message"
        @onClick="onSignUpPressWithValidation"
      >
        {{ $t('message.register') }}
      </Button>

      <Button
        v-if="federatedSignIn"
        class="w-80"
        aria-describedby="validation-message"
        @onClick="onFederatedSignUpPressWithValidation"
      >
        {{ $t('message.register') }}
      </Button>

      <div
        v-if="registrationButtonDisabled || federatedRegistrationButtonDisabled"
        id="validation-message"
        class="sr-only"
      >
        {{ validationMessage }}
      </div>

      <div
        class="sr-only"
        aria-live="assertive"
        aria-atomic="true"
      >
        {{ validationAnnouncement }}
      </div>

      <Message
        v-show="signUpError"
        class="w-full"
        :text="messageText"
        type="error"
      />
    </form>
    <Message
      v-show="signUpSucced"
      class="-mt-4 w-full sm:w-8/12"
      :text="$t('message.sign_up_email_sent', { email: user.email })"
    />
  </div>
</template>

<script lang="ts">
import Button from '@/components/atoms/Button.vue';
import Input from '@/components/atoms/Input.vue';
import moment from 'moment';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import PhoneNumber from '@/components/atoms/PhoneNumber.vue';
import IconArrowLeft from '@/components/atoms/IconArrowLeft.vue';
import Logo from '@/components/atoms/Logo.vue';
import Message from '@/components/atoms/Message.vue';
import PasswordInput from '@/components/atoms/PasswordInput.vue';
import Select from '@/components/atoms/Select.vue';
import Switch from '@/components/atoms/Switch.vue';
import { ROUTES_NAMES } from '@/constants';
import { CognitoException } from '@/model/cognito';
import { dateCollector } from '@/model/date';
import { AppStatus } from '@/model/status';
import { User } from '@/model/user';
import { cleanUserFromCognito, getCurrentUser, revokeTokens, signUp } from '@/service/cognitoService';
import { federatedRegistration } from '@/service/crmService';
import { InputValidator } from '@/utils';

const ADULT_AGE = 18;

export default defineComponent({
  name: 'RegistrationPanel',
  components: {
    Button,
    Input,
    PhoneNumber,
    Select,
    Switch,
    Message,
    IconArrowLeft,
    Logo,
    PasswordInput
  },
  data() {
    return {
      user: {
        email: '',
        phoneNumber: '',
        password: '',
        name: '',
        surname: '',
        birthdate: '',
        profiling: '',
        marketing: '',
        third: '',
        clientId: ''
      } as User,
      phoneNumber: {
        countryCode: '',
        number: ''
      },
      confirmPassword: '',
      dayBirthdate: this.$t('message.dd'),
      monthBirthdate: this.$t('message.mm'),
      yearBirthdate: this.$t('message.yyyy'),
      defaultYearBirthdateText: this.$t('message.yyyy'),
      defaultYearBirthdateValue: this.$t('message.defaultYearBirthdate'),
      dateCollector: dateCollector,
      acceptProfilingPermission: false,
      acceptMarketingPermission: false,
      acceptThirdPermission: false,
      signUpSucced: false,
      signUpError: false,
      messageText: '',
      isValidPassword: false,
      passwordRequirementsMessage: '',
      federatedSignIn: false,
      username: '',
      countryCode: '',
      marketingTextIsExpanded: false,
      profilingTextIsExpanded: false,
      thirdTextIsExpanded: false,
      previousButtonState: false,
      validationAnnouncement: '',
      showValidationErrors: false
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signUpMessages',
      'clientID',
      'redirectURI',
      'registrationRequestPanelUtils'
    ]),
    linkColor(): string {
      return this.global.linkColor || this.global.primaryColor;
    },

    // Consent descriptions with fallback logic
    marketingConsentDescriptionNotExpanded(): string {
      return this.signUpMessages?.marketingConsentDescriptionNotExpanded ||
             this.$t('message.marketing_consent_description_not_expanded');
    },
    marketingConsentDescriptionExpanded(): string {
      return this.signUpMessages?.marketingConsentDescriptionExpanded ||
             this.$t('message.marketing_consent_description_expanded');
    },
    profilingConsentDescriptionNotExpanded(): string {
      return this.signUpMessages?.profilingConsentDescriptionNotExpanded ||
             this.$t('message.profiling_consent_description_not_expanded');
    },
    profilingConsentDescriptionExpanded(): string {
      return this.signUpMessages?.profilingConsentDescriptionExpanded ||
             this.$t('message.profiling_consent_description_expanded');
    },
    thirdConsentDescriptionNotExpanded(): string {
      return this.signUpMessages?.thirdConsentDescriptionNotExpanded ||
             this.$t('message.third_consent_description_not_expanded');
    },
    thirdConsentDescriptionExpanded(): string {
      return this.signUpMessages?.thirdConsentDescriptionExpanded ||
             this.$t('message.third_consent_description_expanded');
    },

    passwordsMatch(): boolean {
      return this.user.password === this.confirmPassword;
    },
    emailError(): boolean {
      return this.user.email ? !InputValidator.validateEmail(this.user.email) : false;
    },
    nameError(): boolean {
      return this.user.name ? !InputValidator.validateName(this.user.name) : false;
    },
    surnameError(): boolean {
      return this.user.surname ? !InputValidator.validateSurname(this.user.surname) : false;
    },
    emailValid(): boolean {
      return this.user.email ? InputValidator.validateEmail(this.user.email) : false;
    },
    emailFieldError(): boolean {
      return (this.showValidationErrors && !this.user.email) || this.emailError;
    },
    emailErrorMessage(): string {
      if (!this.emailFieldError) {
        return '';
      }

      return this.user.email
        ? this.$t('message.accessibility.email_invalid')
        : this.$t('message.accessibility.registration_email_required');
    },
    nameValid(): boolean {
      return this.user.name ? InputValidator.validateName(this.user.name) : false;
    },
    nameFieldError(): boolean {
      return (this.showValidationErrors && !this.user.name) || this.nameError;
    },
    nameErrorMessage(): string {
      if (!this.nameFieldError) {
        return '';
      }

      return this.user.name
        ? this.$t('message.accessibility.name_invalid')
        : this.$t('message.accessibility.registration_name_required');
    },
    surnameValid(): boolean {
      return this.user.surname ? InputValidator.validateSurname(this.user.surname) : false;
    },
    surnameFieldError(): boolean {
      return (this.showValidationErrors && !this.user.surname) || this.surnameError;
    },
    surnameErrorMessage(): string {
      if (!this.surnameFieldError) {
        return '';
      }

      return this.user.surname
        ? this.$t('message.accessibility.surname_invalid')
        : this.$t('message.accessibility.registration_surname_required');
    },
    passwordFieldError(): boolean {
      return !this.federatedSignIn &&
        this.showValidationErrors &&
        (!this.user.password || !this.isValidPassword);
    },
    passwordErrorMessage(): string {
      if (!this.passwordFieldError) {
        return '';
      }

      return this.user.password
        ? this.$t('message.accessibility.password_invalid')
        : this.$t('message.accessibility.registration_password_required');
    },
    birthdateError(): boolean {
      return this.dayBirthdate === 'GG' || this.monthBirthdate === 'MM' || this.yearBirthdate === 'YYYY';
    },
    birthdateFieldError(): boolean {
      return this.showValidationErrors && this.birthdateError;
    },
    birthdateErrorMessage(): string {
      return this.birthdateFieldError
        ? this.$t('message.accessibility.registration_birthdate_required')
        : '';
    },
    confirmPasswordError(): boolean {
      return this.showValidationErrors && (!this.confirmPassword || !this.passwordsMatch);
    },
    confirmPasswordErrorMessage(): string {
      if (!this.confirmPasswordError) {
        return '';
      }

      return this.confirmPassword
        ? this.$t('message.accessibility.passwords_not_match')
        : this.$t('message.accessibility.confirm_password_required');
    },
    phoneNumberError(): boolean {
      return this.showValidationErrors &&
        !this.phoneNumber.number &&
        this.registrationRequestPanelUtils.phoneNumberRequired;
    },
    phoneNumberErrorMessage(): string {
      return this.phoneNumberError
        ? this.$t('message.accessibility.registration_phone_required')
        : '';
    },
    registrationButtonDisabled(): boolean {
      return !this.user.email ||
        !this.user.name ||
        !this.user.surname ||
        !this.user.password ||
        !this.isValidPassword ||
       (!this.phoneNumber.number && this.registrationRequestPanelUtils.phoneNumberRequired) ||
        !this.passwordsMatch ||
        this.emailError ||
        this.nameError ||
        this.surnameError ||
        this.birthdateError;
    },
    federatedRegistrationButtonDisabled(): boolean {
      return !this.user.email ||
        !this.user.name ||
        !this.user.surname ||
        this.emailError ||
        this.nameError ||
        this.surnameError ||
        this.birthdateError;
    },
    validationMessage(): string {
      const errors: string[] = [];

      // Campi obbligatori mancanti
      if (!this.user.email) {
        errors.push(this.$t('message.accessibility.registration_email_required'));
      } else if (this.emailError) {
        errors.push(this.$t('message.accessibility.email_invalid'));
      }

      if (!this.user.name) {
        errors.push(this.$t('message.accessibility.registration_name_required'));
      } else if (this.nameError) {
        errors.push(this.$t('message.accessibility.name_invalid'));
      }

      if (!this.user.surname) {
        errors.push(this.$t('message.accessibility.registration_surname_required'));
      } else if (this.surnameError) {
        errors.push(this.$t('message.accessibility.surname_invalid'));
      }

      if (!this.federatedSignIn) {
        if (!this.user.password) {
          errors.push(this.$t('message.accessibility.registration_password_required'));
        } else if (!this.isValidPassword) {
          errors.push(this.$t('message.accessibility.password_invalid'));
          // Include specific password requirements when password is invalid
          if (this.passwordRequirementsMessage && this.passwordRequirementsMessage !== this.$t('message.accessibility.password_valid')) {
            errors.push(this.passwordRequirementsMessage);
          }
        }

        if (!this.confirmPassword) {
          errors.push(this.$t('message.accessibility.confirm_password_required'));
        } else if (!this.passwordsMatch) {
          errors.push(this.$t('message.accessibility.passwords_not_match'));
        }
      }

      if (this.birthdateError) {
        errors.push(this.$t('message.accessibility.registration_birthdate_required'));
      }

      if (!this.phoneNumber.number && this.registrationRequestPanelUtils.phoneNumberRequired) {
        errors.push(this.$t('message.accessibility.registration_phone_required'));
      }

      return errors.length > 0
        ? this.$t('message.accessibility.fix_errors', { errors: errors.join('; ') })
        : '';
    }
  },
  watch: {
    registrationButtonDisabled(newVal: boolean, oldVal: boolean) {
      this.previousButtonState = oldVal;
    },
    federatedRegistrationButtonDisabled(newVal: boolean, oldVal: boolean) {
      this.previousButtonState = oldVal;
    }
  },
  async beforeMount() {
    if (this.$route.name === ROUTES_NAMES.federatedLogin) {
      this.federatedSignIn = true;
      try {
        const { username, attributes } = await getCurrentUser();
        this.username = username;
        this.user.email = attributes.email;
        this.user.name = attributes.name;
        this.user.surname = attributes.family_name;
        this.scheduleInitialFocus();
      } catch (error) { }
    }
  },
  mounted() {
    this.scheduleInitialFocus();
  },
  methods: {
    ...mapActions(['setAppStatus', 'setMyEmail', 'startLoading', 'endLoading']),

    scheduleInitialFocus() {
      this.$nextTick(() => {
        window.setTimeout(() => {
          if (!this.shouldSetInitialFocus()) {
            return;
          }

          this.focusInitialField();
        }, 0);
      });
    },
    shouldSetInitialFocus(): boolean {
      const root = this.$el as HTMLElement | undefined;
      const activeElement = document.activeElement as HTMLElement | null;

      if (!root) {
        return false;
      }

      if (!activeElement || activeElement === document.body) {
        return true;
      }

      return !root.contains(activeElement);
    },
    getFocusableInput(refName: string): HTMLElement | null {
      const ref = (this.$refs as Record<string, { $el?: Element } | undefined>)[refName];
      const input = ref?.$el?.querySelector('input') as HTMLInputElement | null;

      if (!input || input.disabled) {
        return null;
      }

      return input;
    },
    focusElement(element: HTMLElement | null): boolean {
      if (!element) {
        return false;
      }

      element.focus();
      return document.activeElement === element;
    },
    focusInitialField(): boolean {
      const orderedRefs = [
        'emailInput',
        'nameInput',
        'surnameInput',
        ...(!this.federatedSignIn ? ['passwordInput', 'confirmPasswordInput'] : [])
      ];

      for (const refName of orderedRefs) {
        if (this.focusElement(this.getFocusableInput(refName))) {
          return true;
        }
      }

      const root = this.$el as HTMLElement | undefined;
      const firstBirthdateSelect = root?.querySelector('select') as HTMLElement | null;
      if (this.focusElement(firstBirthdateSelect)) {
        return true;
      }

      const phoneInput = root?.querySelector('input[type="tel"]') as HTMLElement | null;
      return this.focusElement(phoneInput);
    },
    announceValidationErrors() {
      this.showValidationErrors = true;
      this.validationAnnouncement = '';
      this.$nextTick(() => {
        this.validationAnnouncement = this.validationMessage;
      });
    },
    onSignUpPressWithValidation() {
      if (this.registrationButtonDisabled) {
        this.announceValidationErrors();
        this.$nextTick(() => {
          this.focusFirstInvalidField();
        });
        return;
      }
      this.showValidationErrors = false;
      this.onSignUpPress();
    },
    onFederatedSignUpPressWithValidation() {
      if (this.federatedRegistrationButtonDisabled) {
        this.announceValidationErrors();
        this.$nextTick(() => {
          this.focusFirstInvalidField();
        });
        return;
      }
      this.showValidationErrors = false;
      this.onFederatedSignUpPress();
    },
    focusFirstInvalidField() {
      if (!this.user.email || this.emailError) {
        this.focusElement(this.getFocusableInput('emailInput'));
      } else if (!this.user.name || this.nameError) {
        this.focusElement(this.getFocusableInput('nameInput'));
      } else if (!this.user.surname || this.surnameError) {
        this.focusElement(this.getFocusableInput('surnameInput'));
      } else if (!this.federatedSignIn && (!this.user.password || !this.isValidPassword)) {
        this.focusElement(this.getFocusableInput('passwordInput'));
      } else if (!this.federatedSignIn && (!this.confirmPassword || !this.passwordsMatch)) {
        this.focusElement(this.getFocusableInput('confirmPasswordInput'));
      } else if (this.birthdateError) {
        const daySelect = (this.$el as HTMLElement | undefined)?.querySelector('select') as HTMLElement | null;
        this.focusElement(daySelect);
      } else if (!this.phoneNumber.number && this.registrationRequestPanelUtils.phoneNumberRequired) {
        const phoneInput = (this.$el as HTMLElement | undefined)?.querySelector('input[type="tel"]') as HTMLElement | null;
        this.focusElement(phoneInput);
      }
    },
    async onSignUpPress() {
      try {
        this.messageText = '';
        this.signUpError = false;
        this.user.birthdate = this.formatBirthdate();
        if (!this.isValidBirthdate()) {
          this.messageText = this.$t('message.invalid_birthdate');
          this.signUpError = true;
          return;
        }
        if (!this.isValidAge()) {
          this.messageText = this.$t('message.under_age');
          this.signUpError = true;
          return;
        }
        if (this.phoneNumber.number) {
          this.user.phoneNumber = this.phoneNumber.countryCode + this.phoneNumber.number;
        }

        this.user.profiling = String(this.acceptProfilingPermission);
        this.user.marketing = String(this.acceptMarketingPermission);
        this.user.third = String(this.acceptThirdPermission);
        this.user.email = this.user.email.toLowerCase();
        this.user.name = this.user.name.toUpperCase();
        this.user.surname = this.user.surname.toUpperCase();
        this.startLoading();
        await signUp(this.user);
        this.signUpSucced = true;
      } catch (error: any) {
        this.signUpError = true;
        if (error.code === CognitoException.UsernameExistsException) {
          this.setAppStatus(AppStatus.UserAlreadyExists);
          this.switchToLoginPanel();
        } else if (error.code === CognitoException.InvalidPasswordException ||
          (error.code === CognitoException.InvalidParameterException && error.message.includes('password'))) {
          this.messageText = this.$t('message.invalid_password');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.HatTrickSignUpException)) {
          this.messageText = this.$t('message.hat_trick_message');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMEmailExistedException)) {
          this.messageText = this.$t('message.crm_existing_email');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CustomMessageTriggerException)) {
          this.signUpSucced = true;
        } else if (error.code === CognitoException.InvalidParameterException &&
          error.message.includes(CognitoException.InvalidPhoneNumber)) {
          this.messageText = this.$t('message.invalid_phone_number');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneNotAllowed)) {
          this.messageText = this.$t('message.crm_phone_not_allowed');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneUsed)) {
          this.messageText = this.$t('message.crm_phone_used');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneNotCongruent)) {
          this.messageText = this.$t('message.crm_phone_not_congruent');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMIncongruentData)) {
          this.messageText = this.$t('message.crm_incongruent_data');
        } else {
          this.messageText = this.$t('message.sign_up_error');
        }
      } finally {
        this.endLoading();
        this.user.phoneNumber = '';
      }
    },
    async onFederatedSignUpPress() {
      try {
        this.messageText = '';
        this.signUpError = false;
        this.user.birthdate = this.formatBirthdate();
        if (!this.isValidBirthdate()) {
          this.messageText = this.$t('message.invalid_birthdate');
          this.signUpError = true;
          return;
        }
        if (!this.isValidAge()) {
          this.messageText = this.$t('message.under_age');
          this.signUpError = true;
          return;
        }
        if (this.phoneNumber.number !== '') {
          this.user.phoneNumber = this.phoneNumber.countryCode + this.phoneNumber.number;
        }

        this.user.clientId = this.clientID;
        this.user.profiling = String(this.acceptProfilingPermission);
        this.user.marketing = String(this.acceptMarketingPermission);
        this.user.third = String(this.acceptThirdPermission);
        this.user.email = this.user.email.toLowerCase();
        this.user.name = this.user.name.toUpperCase();
        this.user.surname = this.user.surname.toUpperCase();
        this.startLoading();
        await federatedRegistration(this.user, this.username);
      } catch (error: any) {
        this.signUpError = true;
        console.log(`TEST${error.response?.data?.message}`);
        if (error.response?.data?.message?.includes(CognitoException.HatTrickFederatedSignUpException)) {
          this.messageText = this.$t('message.hat_trick_message');
        }
        else if (error.response?.data?.message?.includes(CognitoException.CRMEmailExistedException)) {
          this.messageText = this.$t('message.crm_existing_email');
        }
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneNotAllowed)) {
          this.messageText = this.$t('message.crm_phone_not_allowed');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneUsed)) {
          this.messageText = this.$t('message.crm_phone_used');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneNotCongruent)) {
          this.messageText = this.$t('message.crm_phone_not_congruent');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMIncongruentData)) {
          this.messageText = this.$t('message.crm_incongruent_data');
        } 
        else {
          this.messageText = this.$t('message.sign_up_error');
        }
      } finally {
        this.endLoading();
        this.user.phoneNumber = '';
      }
    },
    async switchToLoginPanel() {
      if (this.user.email) {
        this.setMyEmail(this.user.email);
      }

      if (this.federatedSignIn) {
        try {
          await cleanUserFromCognito();
        } catch (error) {
          console.error(error);
        }
        await revokeTokens();
        return this.$router.push({
          path: 'login',
          query: {
            client_id: this.clientID,
            redirect_uri: this.redirectURI
          }
        });
      }

      this.$router.push({
        path: 'login',
        query: { ...this.$route.query }
      });
    },
    formatBirthdate(): string {
      return this.$t('message.date_format', {
        day: Number(this.dayBirthdate) < 10 ? `0${this.dayBirthdate}` : this.dayBirthdate,
        month: Number(this.monthBirthdate) < 10 ? `0${this.monthBirthdate}` : this.monthBirthdate,
        year: this.yearBirthdate
      });
    },
    isValidBirthdate(): boolean {
      return moment(this.user.birthdate).isValid();
    },
    isValidAge(): boolean {
      return moment().diff(this.user.birthdate, 'years') >= ADULT_AGE;
    },
    updatePasswordStatus(isValid: boolean) {
      this.isValidPassword = isValid;
    },
    updatePasswordRequirements(requirementsMessage: string) {
      this.passwordRequirementsMessage = requirementsMessage;
    }
  },
});
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.input-error-message {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #D20000;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
}
</style>
